import { useMemo, useRef, useState } from 'react';

/**
 * A request counter: `begin()` starts a request and returns `isCurrent()`, false once a later request (or an
 * `invalidate()`) has started, so a slower earlier answer never overwrites the one now on screen.
 */
export const useLatestRequest = () => {
    const latest = useRef(0);

    return useMemo(
        () => ({
            begin: () => {
                const request = ++latest.current;
                return () => request === latest.current;
            },
            invalidate: () => {
                latest.current += 1;
            },
        }),
        []
    );
};

const loading = () => ({ status: 'loading', data: null, error: null });

/** True for the two states the error Alert shows: a failed call, or a body that did not parse. */
export const isLoadFailed = load => load.status === 'error' || load.status === 'invalid';

/**
 * One GET with its validated body: `load` = {status: loading | ready | invalid | error, data, error}, and `run`
 * to start (or retry) it. `request` makes the call, `parse` returns the body or null for a shape it cannot
 * render (→ invalid). `keepData` keeps the last body on screen while reloading; `onReady` runs after a valid body.
 * Only the latest call may settle `load`.
 */
export const useVinRequest = ({ request, parse, keepData = false, onReady }) => {
    const [load, setLoad] = useState(loading);
    const latest = useLatestRequest();

    const run = async () => {
        const isCurrent = latest.begin();
        setLoad(prev => (keepData ? { ...prev, status: 'loading', error: null } : loading()));
        try {
            const data = parse(await request());
            if (!isCurrent()) return;
            if (!data) return setLoad({ status: 'invalid', data: null, error: null });
            setLoad({ status: 'ready', data, error: null });
            onReady?.();
        } catch (error) {
            if (!isCurrent()) return;
            setLoad({ status: 'error', data: null, error });
        }
    };

    return { load, run };
};
